import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { passwordAPI } from '../lib/api';
import { Lock, Eye, EyeOff, RefreshCw, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { toast.error('Passwords do not match'); return; }
    if (password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setLoading(true);
    try {
      await passwordAPI.resetPassword(token, password);
      setDone(true);
      toast.success('Password reset successfully!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reset failed. Token may be invalid or expired.');
    } finally { setLoading(false); }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center p-4">
        <div className="bg-white rounded-xl border border-[#DADCE0] p-8 max-w-md text-center">
          <h1 className="text-xl font-semibold text-[#202124] mb-4">Invalid Reset Link</h1>
          <p className="text-[#5F6368] mb-4">This password reset link is invalid or has expired.</p>
          <Link to="/forgot-password" className="text-[#1A73E8] font-medium hover:underline">Request a new one</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-xl border border-[#DADCE0] p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto bg-[#E6F4EA] rounded-full flex items-center justify-center mb-4">
              {done ? <CheckCircle className="w-8 h-8 text-[#1E8E3E]" /> : <Lock className="w-8 h-8 text-[#1A73E8]" />}
            </div>
            <h1 className="text-2xl font-semibold text-[#202124]" style={{ fontFamily: 'Outfit' }}>
              {done ? 'Password Reset!' : 'Set New Password'}
            </h1>
          </div>

          {!done ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#5F6368] mb-1">New Password</label>
                <div className="relative">
                  <input type={showPass ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)}
                    className="input-google pr-10" required minLength={6} data-testid="reset-password" />
                  <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5F6368]">
                    {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#5F6368] mb-1">Confirm Password</label>
                <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                  className="input-google" required minLength={6} data-testid="reset-confirm" />
              </div>
              <button type="submit" disabled={loading} className="btn-google w-full flex items-center justify-center gap-2" data-testid="reset-submit">
                {loading ? <RefreshCw className="w-5 h-5 animate-spin" /> : null}
                {loading ? 'Resetting...' : 'Reset Password'}
              </button>
            </form>
          ) : (
            <div className="text-center">
              <p className="text-[#5F6368] mb-4">Your password has been reset. You can now sign in.</p>
              <button onClick={() => navigate('/login')} className="btn-google" data-testid="go-to-login">Go to Login</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
